/**
 * Smart Money Unified Score — "Điểm Smart Money tổng hợp"
 *
 * Aggregates money flow, risk, accumulation, RS and divergence
 * into a single 0-100 score per stock.
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box, Typography, Button, CircularProgress, alpha, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Snackbar, Alert, Drawer, Divider, IconButton,
  TextField, InputAdornment, Skeleton,
} from '@mui/material';
import {
  Refresh, Close, Search, TrendingUp, Diamond,
  ArrowUpward, ArrowDownward,
  Assessment, LocalFireDepartment, ShoppingCart,
  Visibility, RemoveCircleOutline, Speed, ImageOutlined,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ReferenceLine,
} from 'recharts';
import html2canvas from 'html2canvas';
import { smartMoneyUnifiedAPI } from '../services/api';
import { useTranslation } from 'react-i18next';
import MarketContextStrip from '../components/MarketContextStrip';

// ── Helpers ──────────────────────────────────────────────────────
const fmtScore = (n) => n != null ? n.toFixed(1) : '—';
const fmtTurnover = (n) => {
  if (n == null) return '—';
  if (Math.abs(n) >= 1e12) return `${(n / 1e12).toFixed(1)}T`;
  if (Math.abs(n) >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  return Number(n).toLocaleString();
};
const localizeCopy = (value, lang) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value[lang] || value.vi || value.en || '';
};
const smartMoneyScoreColor = (score) => (
  score >= 75 ? '#10b981' : score >= 60 ? '#3b82f6' : score >= 45 ? '#f59e0b' : '#94a3b8'
);
const liquidityMeta = (avgValue20d, t) => {
  if (avgValue20d == null) return { label: t('smartMoney.detail.unknown'), color: '#94a3b8' };
  if (avgValue20d >= 20e9) return { label: t('smartMoney.detail.liquidityHigh'), color: '#10b981' };
  if (avgValue20d >= 7e9) return { label: t('smartMoney.detail.liquidityMedium'), color: '#3b82f6' };
  return { label: t('smartMoney.detail.liquidityLow'), color: '#f59e0b' };
};
const investorVerdict = (row, t) => {
  if (row.signal_strong_buy) {
    return {
      title: t('smartMoney.detail.verdictStrongBuy'),
      body: t('smartMoney.detail.verdictStrongBuyBody'),
      color: '#10b981',
    };
  }
  if (row.signal_buy) {
    return {
      title: t('smartMoney.detail.verdictBuy'),
      body: t('smartMoney.detail.verdictBuyBody'),
      color: '#3b82f6',
    };
  }
  if ((row.vpa_ready_to_buy || row.signal_accumulation) && (row.smart_money_score || 0) >= 45) {
    return {
      title: t('smartMoney.detail.verdictAccumulate'),
      body: t('smartMoney.detail.verdictAccumulateBody'),
      color: '#f59e0b',
    };
  }
  return {
    title: t('smartMoney.detail.verdictWatch'),
    body: t('smartMoney.detail.verdictWatchBody'),
    color: '#64748b',
  };
};

const LABEL_CFG = {
  STRONG_BUY: { color: '#10b981', bg: '#ecfdf5', key: 'smartMoney.labels.strongBuy' },
  BUY:        { color: '#3b82f6', bg: '#eff6ff', key: 'smartMoney.labels.buy' },
  WATCH:      { color: '#f59e0b', bg: '#fffbeb', key: 'smartMoney.labels.watch' },
  NEUTRAL:    { color: '#94a3b8', bg: '#f8fafc', key: 'smartMoney.labels.neutral' },
  WEAK:       { color: '#ef4444', bg: '#fef2f2', key: 'smartMoney.labels.weak' },
};

// ── Skeleton loaders ─────────────────────────────────────────────
const StatSkeleton = () => (
  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 1.2, mb: 2 }}>
    {[1,2,3,4,5,6,7].map(i => (
      <Box key={i} sx={{ p: 1.5, borderRadius: 1.5, border: '1px solid #f1f5f9' }}>
        <Skeleton width={50} height={10} sx={{ mb: 0.5 }} />
        <Skeleton width={40} height={22} />
      </Box>
    ))}
  </Box>
);
const TableSkeleton = () => (
  <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
    {/* Search bar skeleton */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, px: 1.5, pt: 1.5 }}>
      <Skeleton variant="rounded" height={32} sx={{ borderRadius: 2, flex: 2 }} />
      {[1,2,3,4].map(i => <Skeleton key={i} variant="rounded" height={24} sx={{ borderRadius: 1.5, flex: 1 }} />)}
    </Box>
    {/* Table header */}
    <Box sx={{ bgcolor: '#f8fafc', px: 1.5, py: 0.8, display: 'flex', gap: 1.5 }}>
      {[1,1,1.5,2,1.5,1.5,1.5,1,1.5,3].map((f, i) => <Skeleton key={i} height={14} sx={{ flex: f }} />)}
    </Box>
    {/* Table rows */}
    {[1,2,3,4,5,6,7,8].map(r => (
      <Box key={r} sx={{ px: 1.5, py: 0.7, display: 'flex', gap: 1.5, borderTop: '1px solid #f1f5f9' }}>
        {[1,1,1.5,2,1.5,1.5,1.5,1,1.5,3].map((f, i) => <Skeleton key={i} height={14} sx={{ flex: f }} />)}
      </Box>
    ))}
  </Box>
);

// ── Stat tile ────────────────────────────────────────────────────
const StatTile = ({ label, value, color, sub, Icon }) => (
  <Box sx={{
    position: 'relative', overflow: 'hidden',
    borderRadius: 2, border: `1px solid ${alpha(color, 0.15)}`,
    bgcolor: '#fff', p: '14px 16px', transition: 'all 0.2s',
    '&:hover': { borderColor: alpha(color, 0.3), boxShadow: `0 4px 16px ${alpha(color, 0.08)}`, transform: 'translateY(-1px)' },
  }}>
    <Box sx={{ position: 'absolute', top: 0, right: 0, width: 64, height: 64,
      background: `radial-gradient(circle at top right, ${alpha(color, 0.06)} 0%, transparent 70%)` }} />
    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      {Icon && (
        <Box sx={{ width: 36, height: 36, borderRadius: 2, flexShrink: 0,
          background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: `0 3px 8px ${alpha(color, 0.25)}` }}>
          <Icon sx={{ fontSize: 18, color: '#fff' }} />
        </Box>
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: '0.5px', color: '#94a3b8', lineHeight: 1, mb: 0.5 }}>{label}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.4 }}>
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 900, color, fontFamily: 'monospace', lineHeight: 1 }}>{value}</Typography>
          {sub && <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', fontWeight: 500 }}>{sub}</Typography>}
        </Box>
      </Box>
    </Box>
  </Box>
);

// ── Search field ─────────────────────────────────────────────────
const SearchField = ({ value, onChange, placeholder }) => (
  <TextField size="small" value={value} onChange={e => onChange(e.target.value)}
    placeholder={placeholder} variant="outlined"
    InputProps={{
      startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment>,
      sx: { fontSize: '0.78rem', borderRadius: 2, height: 32, '& fieldset': { borderColor: '#e2e8f0' } },
    }}
    sx={{ width: 180 }}
  />
);

// ── Component mini bar ───────────────────────────────────────────
const MiniBar = ({ label, value, max = 100, color }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.4 }}>
    <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', width: 70, flexShrink: 0 }}>{label}</Typography>
    <Box sx={{ flex: 1, height: 6, bgcolor: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
      <Box sx={{ width: `${Math.min(100, (value || 0) / max * 100)}%`, height: '100%', bgcolor: color, borderRadius: 3 }} />
    </Box>
    <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, fontFamily: 'monospace', color, width: 32, textAlign: 'right' }}>{fmtScore(value)}</Typography>
  </Box>
);

const DrawerCard = ({ title, subtitle, children, sx = {} }) => (
  <Box sx={{ borderRadius: 2, bgcolor: '#fff', border: '1px solid #e2e8f0', overflow: 'hidden', ...sx }}>
    {title && (
      <Box sx={{ px: 1.5, py: 0.85, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#475569' }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ fontSize: '0.63rem', color: '#94a3b8', mt: 0.2 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    )}
    <Box sx={{ p: 1.4 }}>
      {children}
    </Box>
  </Box>
);

const DetailStat = ({ label, value, sub, color = '#1e293b' }) => (
  <Box sx={{ p: 1.25, borderRadius: 1.5, bgcolor: '#fff', border: '1px solid #e2e8f0' }}>
    <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.4 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color, lineHeight: 1.2 }}>
      {value}
    </Typography>
    {sub && (
      <Typography sx={{ fontSize: '0.66rem', color: '#94a3b8', mt: 0.2, lineHeight: 1.35 }}>
        {sub}
      </Typography>
    )}
  </Box>
);

const InsightRow = ({ label, value, tone = '#1e293b' }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2, py: 0.8, borderBottom: '1px solid #f1f5f9', '&:last-child': { borderBottom: 'none', pb: 0 } }}>
    <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: '0.74rem', color: tone, fontWeight: 700, textAlign: 'right', lineHeight: 1.4 }}>
      {value || '—'}
    </Typography>
  </Box>
);

// ── Score cell with bar + label ──────────────────────────────
const scoreLevel = (v, t) => {
  if (v == null) return { text: '—', color: '#94a3b8' };
  if (v >= 80) return { text: t('smartMoney.table.levelVeryStrong'), color: '#059669' };
  if (v >= 65) return { text: t('smartMoney.table.levelStrong'), color: '#10b981' };
  if (v >= 50) return { text: t('smartMoney.table.levelGood'), color: '#3b82f6' };
  if (v >= 35) return { text: t('smartMoney.table.levelAverage'), color: '#f59e0b' };
  return { text: t('smartMoney.table.levelWeak'), color: '#ef4444' };
};

const ScoreCell = ({ value, t }) => {
  const { text, color } = scoreLevel(value, t);
  return (
    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color }}>{text}</Typography>
  );
};
// ═══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════

export default function SmartMoneyUnified() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('vi') ? 'vi' : 'en';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [sortCol, setSortCol] = useState('smart_money_score');
  const [sortAsc, setSortAsc] = useState(false);
  const [detailRow, setDetailRow] = useState(null);
  const [snack, setSnack] = useState(null);
  const [filterLabel, setFilterLabel] = useState(null);
  const [filterSector, setFilterSector] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPayload, setHistoryPayload] = useState(null);
  const [exportingImage, setExportingImage] = useState(false);
  const drawerCaptureRef = useRef(null);
  const drawerScrollRef = useRef(null);

  const results = React.useMemo(() => data?.results || [], [data?.results]);
  const summary = data?.summary || {};

  // ── Sort & filter ───────────────────────────────────────────
  const toggleSort = (col) => {
    if (sortCol === col) setSortAsc(!sortAsc);
    else { setSortCol(col); setSortAsc(false); }
  };
  const SortIcon = ({ col }) => {
    if (sortCol !== col) return null;
    return sortAsc
      ? <ArrowUpward sx={{ fontSize: 12, ml: 0.3, verticalAlign: 'middle' }} />
      : <ArrowDownward sx={{ fontSize: 12, ml: 0.3, verticalAlign: 'middle' }} />;
  };

  // Sector helpers — sector is {vi, en} or legacy string
  const sectorName = (sec) => {
    if (!sec) return '—';
    if (typeof sec === 'object') return sec[lang] || sec.en || '—';
    return sec;
  };
  const sectorKey = (sec) => {
    if (!sec) return null;
    if (typeof sec === 'object') return sec.en || sec.vi || null;
    return sec;
  };

  // Unique sectors for filter
  const sectors = React.useMemo(() => {
    const map = new Map();
    results.forEach(r => {
      const key = sectorKey(r.sector);
      if (key && !map.has(key)) map.set(key, r.sector);
    });
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [results]);

  const filtered = results
    .filter(r => !search || r.ticker?.toLowerCase().includes(search.toLowerCase()))
    .filter(r => !filterLabel || r.smart_money_label === filterLabel)
    .filter(r => !filterSector || sectorKey(r.sector) === filterSector)
    .sort((a, b) => {
      const va = a[sortCol] ?? 0, vb = b[sortCol] ?? 0;
      return sortAsc ? va - vb : vb - va;
    });

  // ── Scan ────────────────────────────────────────────────────
  const runScan = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await smartMoneyUnifiedAPI.scan({ top_n: 50, min_score: 30, persist: true });
      setData(resp.data);
      setSnack({ severity: 'success', msg: t('smartMoney.messages.scanSuccess', { count: resp.data.total }) });
    } catch (e) {
      setError(e.message);
      setSnack({ severity: 'error', msg: t('smartMoney.messages.scanError') });
    } finally {
      setLoading(false);
    }
  }, [t]);

  // ── Load cached ─────────────────────────────────────────────
  const loadCached = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await smartMoneyUnifiedAPI.getTop({ top_n: 50, min_score: 30 });
      setData(resp.data);
      if (resp.data.total === 0) {
        setSnack({ severity: 'info', msg: t('smartMoney.messages.noData') });
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Auto-load on mount
  React.useEffect(() => { loadCached(); }, [loadCached]);

  useEffect(() => {
    if (!detailRow?.ticker) {
      setHistoryPayload(null);
      setHistoryLoading(false);
      return;
    }

    let active = true;
    setHistoryLoading(true);
    smartMoneyUnifiedAPI.getTickerHistory(detailRow.ticker, { days: 30 })
      .then((resp) => {
        if (!active) return;
        setHistoryPayload(resp.data || null);
      })
      .catch(() => {
        if (!active) return;
        setHistoryPayload(null);
      })
      .finally(() => {
        if (active) setHistoryLoading(false);
      });

    return () => {
      active = false;
    };
  }, [detailRow?.ticker]);

  const handleExportDrawerImage = useCallback(async () => {
    if (!drawerCaptureRef.current || !drawerScrollRef.current || !detailRow || exportingImage) {
      return;
    }

    const captureNode = drawerCaptureRef.current;
    const scrollNode = drawerScrollRef.current;
    const paperNode = captureNode.parentElement;
    const previousOverflow = scrollNode.style.overflowY;
    const previousHeight = scrollNode.style.height;
    const previousMaxHeight = scrollNode.style.maxHeight;
    const previousFlex = scrollNode.style.flex;
    const previousCaptureHeight = captureNode.style.height;
    const previousPaperHeight = paperNode?.style.height || '';
    const previousPaperMaxHeight = paperNode?.style.maxHeight || '';
    const previousPaperOverflow = paperNode?.style.overflow || '';
    const previousPaperOverflowY = paperNode?.style.overflowY || '';

    try {
      setExportingImage(true);
      if (paperNode) {
        paperNode.style.height = 'auto';
        paperNode.style.maxHeight = 'none';
        paperNode.style.overflow = 'visible';
        paperNode.style.overflowY = 'visible';
      }
      scrollNode.style.overflowY = 'visible';
      scrollNode.style.height = 'auto';
      scrollNode.style.maxHeight = 'none';
      scrollNode.style.flex = '0 0 auto';
      captureNode.style.height = `${captureNode.scrollHeight}px`;

      await new Promise((resolve) => window.requestAnimationFrame(resolve));

      const canvas = await html2canvas(captureNode, {
        backgroundColor: '#fafbfc',
        scale: 2,
        useCORS: true,
        width: captureNode.scrollWidth,
        height: captureNode.scrollHeight,
        windowWidth: captureNode.scrollWidth,
        windowHeight: captureNode.scrollHeight,
        scrollX: 0,
        scrollY: 0,
      });

      const link = document.createElement('a');
      const asOfDate = detailRow?.date || data?.scan_date || new Date().toISOString().slice(0, 10);
      link.download = `${detailRow?.ticker || 'smart-money'}-drawer-${asOfDate}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Failed to export smart money drawer image', error);
    } finally {
      scrollNode.style.overflowY = previousOverflow;
      scrollNode.style.height = previousHeight;
      scrollNode.style.maxHeight = previousMaxHeight;
      scrollNode.style.flex = previousFlex;
      captureNode.style.height = previousCaptureHeight;
      if (paperNode) {
        paperNode.style.height = previousPaperHeight;
        paperNode.style.maxHeight = previousPaperMaxHeight;
        paperNode.style.overflow = previousPaperOverflow;
        paperNode.style.overflowY = previousPaperOverflowY;
      }
      setExportingImage(false);
    }
  }, [data?.scan_date, detailRow, exportingImage]);

  const labelDist = summary?.label_distribution || {};

  return (
    <Box sx={{ p: 2.5, maxWidth: 1480, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: 2.5,
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(16,185,129,0.25)',
          }}>
            <Diamond sx={{ fontSize: 26, color: '#fff' }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#1e293b', letterSpacing: -0.3 }}>
                {t('smartMoney.title')}
              </Typography>
              {data?.scan_date && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#f1f5f9', px: 1, py: 0.3, borderRadius: 1.5 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#10b981', boxShadow: '0 0 6px #10b981' }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>{data.scan_date}</Typography>
                </Box>
              )}
            </Box>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>
              {t('smartMoney.subtitle')}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={loadCached} disabled={loading}
            sx={{
              textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '6px',
              color: '#64748b', borderColor: '#e2e8f0', px: 2,
              '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' },
              '&:disabled': { bgcolor: '#f1f5f9', color: '#94a3b8', borderColor: '#e2e8f0' },
            }}
          >
            {t('smartMoney.actions.reload')}
          </Button>
          <Button
            variant="outlined"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Refresh sx={{ fontSize: 16 }} />}
            onClick={runScan} disabled={loading}
            sx={{
              textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '6px',
              color: '#059669', borderColor: '#6ee7b7', px: 2,
              '&:hover': { bgcolor: '#ecfdf5', borderColor: '#10b981' },
              '&:disabled': { bgcolor: '#f1f5f9', color: '#94a3b8', borderColor: '#e2e8f0' },
            }}
          >
            {loading ? t('smartMoney.actions.scanning') : t('smartMoney.actions.scanNew')}
          </Button>
        </Box>
      </Box>

      {/* Market Context Strip */}
      <MarketContextStrip standalone />

      {/* Summary stats */}
      {!data && loading ? <StatSkeleton /> : data && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)', md: 'repeat(7, 1fr)' }, gap: 1.5, mb: 2.5 }}>
          <StatTile Icon={Assessment} label={t('smartMoney.stats.total')} value={summary.total || 0} color="#1e293b" />
          <StatTile Icon={LocalFireDepartment} label={t('smartMoney.labels.strongBuy')} value={labelDist.STRONG_BUY || 0} color="#10b981" />
          <StatTile Icon={ShoppingCart} label={t('smartMoney.labels.buy')} value={labelDist.BUY || 0} color="#3b82f6" />
          <StatTile Icon={Visibility} label={t('smartMoney.labels.watch')} value={labelDist.WATCH || 0} color="#f59e0b" />
          <StatTile Icon={RemoveCircleOutline} label={t('smartMoney.labels.neutral')} value={labelDist.NEUTRAL || 0} color="#94a3b8" />
          <StatTile Icon={TrendingUp} label={t('smartMoney.labels.weak')} value={labelDist.WEAK || 0} color="#ef4444" />
          {summary.avg_score != null && (
            <StatTile Icon={Speed} label={t('smartMoney.stats.avg')} value={summary.avg_score.toFixed(1)} color="#6366f1" sub="/100" />
          )}
        </Box>
      )}

      {/* Search + label pills + sector filter */}
      {data && results.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap' }}>
            <SearchField value={search} onChange={setSearch} placeholder={t('smartMoney.search.placeholder')} />
            {Object.entries(labelDist).filter(([,c]) => c > 0).map(([key, count]) => {
              const cfg = LABEL_CFG[key] || LABEL_CFG.NEUTRAL;
              const active = filterLabel === key;
              return (
                <Box key={key} onClick={() => setFilterLabel(active ? null : key)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.3, px: 0.7, py: 0.3, borderRadius: 1.5, cursor: 'pointer',
                    bgcolor: active ? alpha(cfg.color, 0.18) : cfg.bg,
                    border: `1.5px solid ${alpha(cfg.color, active ? 0.5 : 0.15)}`,
                    transition: 'all 0.15s' }}>
                  <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: cfg.color }}>{t(cfg.key)}</Typography>
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 900, color: cfg.color, fontFamily: 'monospace' }}>{count}</Typography>
                </Box>
              );
            })}
            {/* Sector pills */}
            {sectors.length > 0 && (
              <>
                <Box sx={{ width: '1px', height: 16, bgcolor: '#e2e8f0', mx: 0.2 }} />
                {sectors.slice(0, 8).map(([key, secObj]) => (
                  <Chip key={key} label={sectorName(secObj)} size="small" onClick={() => setFilterSector(filterSector === key ? null : key)}
                    sx={{ fontSize: '0.6rem', fontWeight: 600, height: 20, cursor: 'pointer',
                      bgcolor: filterSector === key ? alpha('#6366f1', 0.15) : '#f1f5f9',
                      color: filterSector === key ? '#4f46e5' : '#64748b',
                      border: `1px solid ${filterSector === key ? '#6366f1' : '#e2e8f0'}`,
                      transition: 'all 0.15s' }} />
                ))}
              </>
            )}
          </Box>
          <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>
            <Box component="span" sx={{ fontWeight: 700, color: '#475569' }}>{filtered.length}</Box> / {results.length}
          </Typography>
        </Box>
      )}

      {/* Loading / Error / Empty */}
      {loading && !data && <TableSkeleton />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {data && results.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 6, color: '#94a3b8' }}>
          <Diamond sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
          <Typography sx={{ fontWeight: 600 }}>{t('smartMoney.empty.title')}</Typography>
          <Typography sx={{ fontSize: '0.8rem' }}>{t('smartMoney.empty.subtitle')}</Typography>
        </Box>
      )}

      {/* Table */}
      {filtered.length > 0 && (
        <TableContainer sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b', width: 36 }}>{t('smartMoney.table.rank')}</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b' }}>{t('smartMoney.table.ticker')}</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b' }}>{t('smartMoney.table.price')}</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b', cursor: 'pointer' }} onClick={() => toggleSort('smart_money_score')}>
                  {t('smartMoney.table.smScore')} <SortIcon col="smart_money_score" />
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b' }}>{t('smartMoney.table.label')}</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b', cursor: 'pointer' }} onClick={() => toggleSort('money_flow_score')}>
                  {t('smartMoney.table.moneyFlow')} <SortIcon col="money_flow_score" />
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b', cursor: 'pointer' }} onClick={() => toggleSort('safety_score')}>
                  {t('smartMoney.table.safety')} <SortIcon col="safety_score" />
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b', cursor: 'pointer' }} onClick={() => toggleSort('rs_rank_score')}>
                  {t('smartMoney.table.rs')} <SortIcon col="rs_rank_score" />
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b' }}>{t('smartMoney.table.signals')}</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b' }}>{t('smartMoney.table.reasons')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((r, i) => {
                const lbl = LABEL_CFG[r.smart_money_label] || LABEL_CFG.NEUTRAL;
                const scoreColor = smartMoneyScoreColor(r.smart_money_score);
                return (
                  <TableRow key={r.ticker} hover onClick={() => setDetailRow(r)}
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: alpha('#10b981', 0.03) } }}>
                    <TableCell sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>{i + 1}</TableCell>
                    <TableCell><Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#1e293b' }}>{r.ticker}</Typography></TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600, fontFamily: 'monospace' }}>{r.close?.toLocaleString() || '—'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'inline-block', px: 1, py: 0.2, borderRadius: 1, bgcolor: alpha(scoreColor, 0.08), color: scoreColor, fontSize: '0.82rem', fontWeight: 800, fontFamily: 'monospace' }}>
                        {fmtScore(r.smart_money_score)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: lbl.color }}>{t(lbl.key)}</Typography>
                    </TableCell>
                    <TableCell>
                      <ScoreCell value={r.money_flow_score} t={t} />
                    </TableCell>
                    <TableCell>
                      <ScoreCell value={r.safety_score} t={t} />
                    </TableCell>
                    <TableCell>
                      <ScoreCell value={r.rs_rank_score} t={t} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.3, flexWrap: 'wrap' }}>
                        {r.signal_strong_buy && <Chip label={t('smartMoney.table.signalStrongBuy')} size="small" sx={{ fontSize: '0.55rem', fontWeight: 700, height: 18, bgcolor: alpha('#10b981', 0.1), color: '#10b981' }} />}
                        {r.signal_buy && !r.signal_strong_buy && <Chip label={t('smartMoney.table.signalBuy')} size="small" sx={{ fontSize: '0.55rem', fontWeight: 700, height: 18, bgcolor: alpha('#3b82f6', 0.1), color: '#3b82f6' }} />}
                        {r.signal_accumulation && <Chip label={t('smartMoney.table.signalAccumulate')} size="small" sx={{ fontSize: '0.55rem', fontWeight: 700, height: 18, bgcolor: alpha('#f59e0b', 0.1), color: '#f59e0b' }} />}
                        {!r.signal_strong_buy && !r.signal_buy && !r.signal_accumulation && <Typography sx={{ fontSize: '0.7rem', color: '#cbd5e1' }}>—</Typography>}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.68rem', color: '#64748b', maxWidth: 260, whiteSpace: 'normal', lineHeight: 1.4 }}>
                        {(r.top_reasons || []).slice(0, 3).map(reason =>
                          typeof reason === 'object' ? (reason[lang] || reason.vi || '') : reason
                        ).join('; ')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Detail Drawer ──────────────────────────────────────── */}
      <Drawer
        anchor="right"
        open={!!detailRow}
        onClose={() => setDetailRow(null)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 440, md: 480 }, bgcolor: '#fafbfc' } }}
      >
        {detailRow && (() => {
          const lbl = LABEL_CFG[detailRow.smart_money_label] || LABEL_CFG.NEUTRAL;
          const sc = detailRow.smart_money_score || 0;
          const scoreColor = smartMoneyScoreColor(sc);
          const turnoverView = liquidityMeta(detailRow.avg_value_20d, t);
          const verdict = investorVerdict(detailRow, t);
          const metaSector = historyPayload?.sector || detailRow.sector;
          const companyName = historyPayload?.company_name || detailRow.ticker;
          const asOfDate = detailRow.date || data?.scan_date;
          const orderedHistory = [...(historyPayload?.history || [])].reverse();
          const historyChart = orderedHistory.map((item) => ({
            date: item.date ? item.date.slice(5) : '—',
            score: item.smart_money_score,
            close: item.close,
          }));
          const historyScores = orderedHistory.map((item) => item.smart_money_score).filter((value) => value != null);
          const firstScore = historyScores.length ? historyScores[0] : null;
          const lastScore = historyScores.length ? historyScores[historyScores.length - 1] : null;
          const scoreDelta = firstScore != null && lastScore != null ? lastScore - firstScore : null;
          const highScore = historyScores.length ? Math.max(...historyScores) : null;
          const lowScore = historyScores.length ? Math.min(...historyScores) : null;
          const hasSignalChips = Boolean(
            detailRow.signal_strong_buy
            || detailRow.signal_buy
            || detailRow.signal_accumulation
            || detailRow.vpa_ready_to_buy
            || detailRow.risk_label
            || (metaSector && sectorName(metaSector) !== '—')
            || detailRow.vpa_action_label
          );
          return (
            <Box ref={drawerCaptureRef} sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%', bgcolor: '#fafbfc' }}>
              <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 42, height: 42, borderRadius: 1.5, bgcolor: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
                    <Typography sx={{ fontWeight: 900, color: '#fff', fontSize: '0.9rem' }}>{detailRow.ticker}</Typography>
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', color: '#1e293b' }}>{detailRow.ticker}</Typography>
                      <Chip label={t(lbl.key)} size="small" sx={{ fontWeight: 800, fontSize: '0.62rem', height: 20, bgcolor: lbl.bg, color: lbl.color, border: `1px solid ${alpha(lbl.color, 0.3)}` }} />
                      {detailRow.phase_label && (
                        <Chip label={detailRow.phase_label} size="small" sx={{ fontWeight: 600, fontSize: '0.58rem', height: 18, bgcolor: '#f8fafc', color: '#64748b' }} />
                      )}
                    </Box>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#334155', mt: 0.25, lineHeight: 1.3 }}>
                      {companyName}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.2 }}>
                      {asOfDate && (
                        <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                          {asOfDate}
                        </Typography>
                      )}
                      {detailRow.close != null && (
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.45, px: 0.8, py: 0.25, borderRadius: 1, bgcolor: '#eff6ff', border: '1px solid #dbeafe' }}>
                          <Typography sx={{ fontSize: '0.58rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            {t('smartMoney.detail.price')}
                          </Typography>
                          <Typography sx={{ fontSize: '0.82rem', color: '#2563eb', fontWeight: 800, fontFamily: 'monospace', lineHeight: 1 }}>
                            {detailRow.close.toLocaleString()}
                          </Typography>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.45, px: 0.8, py: 0.25, borderRadius: 1, bgcolor: alpha(scoreColor, 0.08), border: `1px solid ${alpha(scoreColor, 0.16)}` }}>
                        <Typography sx={{ fontSize: '0.58rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          {t('smartMoney.detail.smScore')}
                        </Typography>
                        <Typography sx={{ fontSize: '0.82rem', color: scoreColor, fontWeight: 900, fontFamily: 'monospace', lineHeight: 1 }}>
                          {sc.toFixed(1)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} data-html2canvas-ignore="true">
                  <IconButton size="small" onClick={handleExportDrawerImage} disabled={exportingImage} sx={{ color: '#94a3b8' }}>
                    {exportingImage ? <CircularProgress size={16} color="inherit" /> : <ImageOutlined fontSize="small" />}
                  </IconButton>
                  <IconButton size="small" onClick={() => setDetailRow(null)} sx={{ color: '#94a3b8' }}>
                    <Close />
                  </IconButton>
                </Box>
              </Box>
              <Divider />

              <Box ref={drawerScrollRef} sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', flex: 1 }}>
                <Box sx={{ borderRadius: 2, bgcolor: '#fff', border: '1px solid #e2e8f0' }}>
                  <Box sx={{ px: 1.5, py: 0.85, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', borderTopLeftRadius: 8, borderTopRightRadius: 8 }}>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#475569' }}>
                      {t('smartMoney.detail.snapshotTitle')}
                    </Typography>
                    <Typography sx={{ fontSize: '0.63rem', color: '#94a3b8', mt: 0.2 }}>
                      {t('smartMoney.detail.snapshotSubtitle')}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      p: 1.4,
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                      gap: 1.2,
                    }}
                  >
                    <Box sx={{ p: 1.2, borderRadius: 1.75, border: `1px solid ${alpha(turnoverView.color, 0.18)}`, bgcolor: alpha(turnoverView.color, 0.04), minHeight: 92 }}>
                      <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {t('smartMoney.detail.turnover20d')}
                      </Typography>
                      <Typography sx={{ fontSize: '1.15rem', fontWeight: 900, color: turnoverView.color, lineHeight: 1.2, mt: 0.45 }}>
                        {fmtTurnover(detailRow.avg_value_20d)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: '#64748b', mt: 0.45, lineHeight: 1.45 }}>
                        {turnoverView.label}
                      </Typography>
                    </Box>
                    <Box sx={{ p: 1.2, borderRadius: 1.75, border: `1px solid ${alpha('#3b82f6', 0.18)}`, bgcolor: alpha('#3b82f6', 0.04), minHeight: 92 }}>
                      <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {t('smartMoney.detail.sector')}
                      </Typography>
                      <Typography sx={{ fontSize: '1.15rem', fontWeight: 900, color: '#2563eb', lineHeight: 1.2, mt: 0.45, wordBreak: 'break-word' }}>
                        {sectorName(metaSector)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: '#64748b', mt: 0.45, lineHeight: 1.45 }}>
                        {detailRow.phase_label || t('smartMoney.detail.unknown')}
                      </Typography>
                    </Box>
                    <Box sx={{ p: 1.2, borderRadius: 1.75, border: `1px solid ${alpha(detailRow.risk_label === 'HIGH' || detailRow.risk_label === 'EXTREME' ? '#ef4444' : '#3b82f6', 0.18)}`, bgcolor: alpha(detailRow.risk_label === 'HIGH' || detailRow.risk_label === 'EXTREME' ? '#ef4444' : '#3b82f6', 0.04), minHeight: 92 }}>
                      <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {t('smartMoney.detail.riskProfile')}
                      </Typography>
                      <Typography sx={{ fontSize: '1.15rem', fontWeight: 900, color: detailRow.risk_label === 'HIGH' || detailRow.risk_label === 'EXTREME' ? '#dc2626' : '#2563eb', lineHeight: 1.2, mt: 0.45 }}>
                        {detailRow.risk_label || t('smartMoney.detail.unknown')}
                      </Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: '#64748b', mt: 0.45, lineHeight: 1.45 }}>
                        {`${t('smartMoney.detail.safety')}: ${fmtScore(detailRow.safety_score)}`}
                      </Typography>
                    </Box>
                    <Box sx={{ p: 1.2, borderRadius: 1.75, border: `1px solid ${alpha('#8b5cf6', 0.18)}`, bgcolor: alpha('#8b5cf6', 0.04), minHeight: 92 }}>
                      <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {t('smartMoney.detail.vpa')}
                      </Typography>
                      <Typography sx={{ fontSize: '1.15rem', fontWeight: 900, color: '#7c3aed', lineHeight: 1.2, mt: 0.45 }}>
                        {detailRow.vpa_score != null ? fmtScore(detailRow.vpa_score) : '—'}
                      </Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: '#64748b', mt: 0.45, lineHeight: 1.45, wordBreak: 'break-word' }}>
                        {detailRow.vpa_action_label || detailRow.vpa_setup || t('smartMoney.detail.unknown')}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <DrawerCard
                  title={t('smartMoney.detail.investorLens')}
                  sx={{ bgcolor: alpha(verdict.color, 0.05), borderColor: alpha(verdict.color, 0.2) }}
                >
                  <Typography sx={{ fontSize: '0.86rem', fontWeight: 800, color: verdict.color, mb: 0.35 }}>
                    {verdict.title}
                  </Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: '#475569', lineHeight: 1.55 }}>
                    {verdict.body}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <InsightRow label={t('smartMoney.detail.positioning')} value={t(lbl.key)} tone={lbl.color} />
                    <InsightRow label={t('smartMoney.detail.phase')} value={detailRow.phase_label || t('smartMoney.detail.unknown')} />
                    <InsightRow label={t('smartMoney.detail.liquidity')} value={turnoverView.label} tone={turnoverView.color} />
                    <InsightRow label={t('smartMoney.detail.vpaAction')} value={detailRow.vpa_action_label || t('smartMoney.detail.unknown')} tone="#7c3aed" />
                    <InsightRow label={t('smartMoney.detail.vpaReady')} value={detailRow.vpa_ready_to_buy ? t('smartMoney.detail.yes') : t('smartMoney.detail.no')} tone={detailRow.vpa_ready_to_buy ? '#10b981' : '#94a3b8'} />
                  </Box>
                </DrawerCard>

                <DrawerCard title={t('smartMoney.detail.breakdown')}>
                  <MiniBar label={t('smartMoney.detail.moneyFlow')} value={detailRow.money_flow_score} color="#10b981" />
                  <MiniBar label={t('smartMoney.detail.safety')} value={detailRow.safety_score} color="#3b82f6" />
                  <MiniBar label={t('smartMoney.detail.accumulation')} value={detailRow.accumulation_score} color="#f59e0b" />
                  <MiniBar label={t('smartMoney.detail.rsRank')} value={detailRow.rs_rank_score} color="#8b5cf6" />
                  <MiniBar label={t('smartMoney.detail.divergence')} value={detailRow.divergence_score} color="#06b6d4" />
                  <MiniBar label={t('smartMoney.detail.vpa')} value={detailRow.vpa_score} color="#ec4899" />
                </DrawerCard>

                <DrawerCard title={t('smartMoney.detail.signals')}>
                  {hasSignalChips ? (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {detailRow.signal_strong_buy && <Chip label={t('smartMoney.detail.strongBuy')} size="small" sx={{ fontSize: '0.6rem', fontWeight: 700, height: 20, bgcolor: '#ecfdf5', color: '#10b981' }} />}
                      {detailRow.signal_buy && !detailRow.signal_strong_buy && <Chip label={t('smartMoney.detail.buy')} size="small" sx={{ fontSize: '0.6rem', fontWeight: 700, height: 20, bgcolor: '#eff6ff', color: '#3b82f6' }} />}
                      {detailRow.signal_accumulation && <Chip label={t('smartMoney.detail.accumulate')} size="small" sx={{ fontSize: '0.6rem', fontWeight: 700, height: 20, bgcolor: '#fffbeb', color: '#f59e0b' }} />}
                      {detailRow.vpa_ready_to_buy && <Chip label={t('smartMoney.detail.readyToBuy')} size="small" sx={{ fontSize: '0.6rem', fontWeight: 700, height: 20, bgcolor: alpha('#8b5cf6', 0.08), color: '#7c3aed' }} />}
                      {detailRow.risk_label && <Chip label={`${t('smartMoney.detail.risk')}${detailRow.risk_label}`} size="small" sx={{ fontSize: '0.6rem', fontWeight: 600, height: 20, bgcolor: '#f8fafc', color: '#64748b' }} />}
                      {metaSector && sectorName(metaSector) !== '—' && <Chip label={sectorName(metaSector)} size="small" sx={{ fontSize: '0.6rem', fontWeight: 600, height: 20, bgcolor: '#f8fafc', color: '#64748b' }} />}
                      {detailRow.vpa_action_label && <Chip label={detailRow.vpa_action_label} size="small" sx={{ fontSize: '0.6rem', fontWeight: 600, height: 20, bgcolor: alpha('#8b5cf6', 0.08), color: '#7c3aed' }} />}
                    </Box>
                  ) : (
                    <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                      {t('smartMoney.detail.signalsEmpty')}
                    </Typography>
                  )}
                </DrawerCard>

                <DrawerCard
                  title={t('smartMoney.detail.historyTitle')}
                  subtitle={t('smartMoney.detail.historySubtitle')}
                >
                  {historyLoading ? (
                    <Box>
                      <Skeleton variant="rounded" height={180} sx={{ borderRadius: 1.5, mb: 1 }} />
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 1 }}>
                        {[0, 1, 2, 3].map((idx) => (
                          <Skeleton key={idx} variant="rounded" height={62} sx={{ borderRadius: 1.5 }} />
                        ))}
                      </Box>
                    </Box>
                  ) : historyChart.length > 0 ? (
                    <>
                      <Box sx={{ height: 200, mb: 1.2 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={historyChart} margin={{ top: 6, right: 12, left: -12, bottom: 0 }}>
                            <defs>
                              <linearGradient id="smartMoneyHistoryGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#10b981" stopOpacity={0.18} />
                                <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                            <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                            <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} width={34} />
                            <YAxis yAxisId="price" hide domain={['auto', 'auto']} />
                            <RechartsTooltip
                              formatter={(value, name) => [
                                name === 'close'
                                  ? (value != null ? Number(value).toLocaleString() : '—')
                                  : fmtScore(value),
                                name === 'close' ? t('smartMoney.detail.chartClose') : t('smartMoney.detail.chartScore'),
                              ]}
                              labelFormatter={(label) => label}
                              contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e2e8f0' }}
                            />
                            <ReferenceLine y={75} stroke="#10b981" strokeDasharray="3 3" strokeOpacity={0.45} />
                            <ReferenceLine y={60} stroke="#3b82f6" strokeDasharray="3 3" strokeOpacity={0.35} />
                            <Area type="monotone" dataKey="score" stroke="#10b981" fill="url(#smartMoneyHistoryGrad)" strokeWidth={2.5} />
                            <Line type="monotone" dataKey="score" stroke="#10b981" strokeWidth={2.5} dot={{ r: 2 }} activeDot={{ r: 4 }} />
                            <Line yAxisId="price" type="monotone" dataKey="close" stroke="#94a3b8" strokeWidth={1.5} dot={false} strokeDasharray="4 3" />
                          </AreaChart>
                        </ResponsiveContainer>
                      </Box>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 1 }}>
                        <DetailStat label={t('smartMoney.detail.sessionsTracked')} value={historyChart.length} />
                        <DetailStat
                          label={t('smartMoney.detail.latestChange')}
                          value={scoreDelta != null ? `${scoreDelta >= 0 ? '+' : ''}${scoreDelta.toFixed(1)}` : '—'}
                          color={scoreDelta >= 0 ? '#10b981' : '#ef4444'}
                        />
                        <DetailStat
                          label={t('smartMoney.detail.highestScore')}
                          value={highScore != null ? fmtScore(highScore) : '—'}
                          color="#10b981"
                        />
                        <DetailStat
                          label={t('smartMoney.detail.lowestScore')}
                          value={lowScore != null ? fmtScore(lowScore) : '—'}
                          color="#f59e0b"
                        />
                      </Box>
                    </>
                  ) : (
                    <Alert severity="info" sx={{ fontSize: '0.75rem' }}>
                      {t('smartMoney.detail.historyEmpty')}
                    </Alert>
                  )}
                </DrawerCard>

                {(detailRow.top_reasons || []).length > 0 && (
                  <DrawerCard title={t('smartMoney.detail.reasons')}>
                    {detailRow.top_reasons.map((r, i) => (
                      <Typography key={i} sx={{ fontSize: '0.72rem', color: '#475569', lineHeight: 1.65, mb: i === detailRow.top_reasons.length - 1 ? 0 : 0.35 }}>
                        • {localizeCopy(r, lang)}
                      </Typography>
                    ))}
                  </DrawerCard>
                )}
              </Box>
            </Box>
          );
        })()}
      </Drawer>

      {/* Snackbar */}
      <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {snack && <Alert severity={snack.severity} onClose={() => setSnack(null)} sx={{ fontSize: '0.78rem' }}>{snack.msg}</Alert>}
      </Snackbar>
    </Box>
  );
}
